/**
 * Tax Forms Page
 *
 * Renders Schedule C (sole props / single-LLCs) or Form 1120 (C-Corps) based
 * on the active business profile's entity type for the selected tax year.
 * Entity type is read from biz_tax_years; if none is configured, the user is
 * prompted to set it on the Tax Overview page first.
 *
 * The form-line mapping (which deduction class goes on which line) lives in
 * @bizsolutions/shared/tax/deduction-form-lines.ts.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  FileText,
  Printer,
  Download,
  Building2,
  AlertCircle,
} from 'lucide-react';
import {
  DEDUCTION_FORM_LINES,
  getTaxFormForEntity,
  type DeductionClass,
} from '@bizsolutions/shared';
import { exportScheduleCPDF, exportForm1120PDF } from '@/lib/pdf-export';

interface BusinessProfile {
  id: string;
  name: string;
  taxId: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

interface TaxYearRow {
  tax_year: number;
  business_profile_id: string;
  entity_type: string;
}

interface DeductionRow {
  taxDeductionClass: string;
  totalAmount: number;
  itemCount: number;
}

interface VehicleDetailRow {
  assetId: string;
  vehicleName: string;
  datePlacedInService: string | null;
  vehicleBusinessUsePct: number | null;
  businessMiles: number;
  totalMiles: number;
  deductibleAmount: number;
  expenseCount: number;
  electedMethod: 'mileage' | 'actual' | 'mixed' | null;
}

interface TaxFormData {
  businessProfile: BusinessProfile | null;
  grossReceipts: number;
  taxCollected: number;
  cogs: number;
  grossProfit: number;
  deductions: DeductionRow[];
  totalDeductions: number;
  netProfit: number;
  vehicles: VehicleDetailRow[];
}

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

/**
 * Apply per-class modifiers (e.g., 50% Meals cap) before rendering.
 */
function applyDeductibilityCaps(rows: DeductionRow[]): DeductionRow[] {
  return rows.map(r => {
    const mapping = DEDUCTION_FORM_LINES[r.taxDeductionClass as DeductionClass];
    if (mapping?.is_meals_50pct) {
      return { ...r, totalAmount: r.totalAmount * 0.5 };
    }
    return r;
  });
}

export function TaxFormsPage() {
  const [taxYear, setTaxYear] = useState(currentYear);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');

  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  // Tax year config (gives us the entity type for this profile + year)
  const { data: taxYearsData } = useQuery({
    queryKey: ['tax-years', selectedProfileId],
    queryFn: () => api.getTaxYears({ businessProfileId: selectedProfileId }),
    enabled: !!selectedProfileId,
  });
  const taxYears = (taxYearsData?.data || []) as TaxYearRow[];
  const activeTaxYear = taxYears.find(ty => ty.tax_year === taxYear);
  const entityType = activeTaxYear?.entity_type;
  const formType = getTaxFormForEntity(entityType);

  // Auto-select first profile
  if (!selectedProfileId && profiles.length > 0) {
    setSelectedProfileId(profiles[0].id);
  }

  // Schedule C
  const { data: scheduleCRaw, isLoading: scheduleCLoading } = useQuery({
    queryKey: ['schedule-c', taxYear, selectedProfileId],
    queryFn: async () => {
      const result = await api.getScheduleC({ taxYear, businessProfileId: selectedProfileId });
      return result.data as TaxFormData;
    },
    enabled: !!selectedProfileId && formType === 'schedule_c',
  });

  // Form 1120
  const { data: form1120Raw, isLoading: form1120Loading } = useQuery({
    queryKey: ['form-1120', taxYear, selectedProfileId],
    queryFn: async () => {
      const result = await api.getForm1120({ taxYear, businessProfileId: selectedProfileId });
      return result.data as TaxFormData;
    },
    enabled: !!selectedProfileId && formType === 'form_1120',
  });

  const formData = formType === 'schedule_c' ? scheduleCRaw : form1120Raw;
  const isLoading = formType === 'schedule_c' ? scheduleCLoading : form1120Loading;
  const handlePrint = () => window.print();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tax Forms</h1>
          <p className="text-muted-foreground">Review and export tax form data for filing</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select Business" />
            </SelectTrigger>
            <SelectContent>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={taxYear.toString()} onValueChange={(v) => setTaxYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={y.toString()}>TY {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!selectedProfileId ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-lg font-medium">Select a Business Profile</p>
            <p className="text-muted-foreground text-sm">Choose a business above to generate tax form data</p>
          </CardContent>
        </Card>
      ) : !activeTaxYear ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-amber-500 mb-3" />
            <p className="text-lg font-medium">Tax Year Not Configured</p>
            <p className="text-muted-foreground text-sm text-center max-w-md mt-2">
              No tax year configuration exists for {profiles.find(p => p.id === selectedProfileId)?.name} for {taxYear}.
              Set up entity type, filing status, and tax rates on the Tax Overview page first.
            </p>
          </CardContent>
        </Card>
      ) : formType === 'unsupported' ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-amber-500 mb-3" />
            <p className="text-lg font-medium">Tax Form Not Supported Yet</p>
            <p className="text-muted-foreground text-sm text-center max-w-md mt-2">
              Entity type <Badge variant="secondary">{entityType}</Badge> requires Form 1120-S (S-Corp) or
              Form 1065 (Partnership). Only Schedule C (sole prop / single-LLC) and Form 1120 (C-Corp)
              are currently generated automatically.
            </p>
          </CardContent>
        </Card>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : formType === 'schedule_c' ? (
        <ScheduleCForm
          data={formData}
          taxYear={taxYear}
          onPrint={handlePrint}
        />
      ) : (
        <Form1120
          data={formData}
          taxYear={taxYear}
          onPrint={handlePrint}
        />
      )}
    </div>
  );
}

// ============================================
// Schedule C renderer (Sole Prop / Single-LLC)
// ============================================
function ScheduleCForm({
  data,
  taxYear,
  onPrint,
}: {
  data: TaxFormData | undefined;
  taxYear: number;
  onPrint: () => void;
}) {
  const deductions = applyDeductibilityCaps(data?.deductions || [])
    .filter(d => DEDUCTION_FORM_LINES[d.taxDeductionClass as DeductionClass]?.schedule_c !== null);

  // Sort by Schedule C line number ('8', '16a', '20b', '24b', '27a', etc.)
  const lineSortKey = (line: string | null | undefined) => {
    if (!line) return 99;
    return parseFloat(line.replace(/[a-z]/g, '.5')) || 99;
  };

  const sortedDeductions = [...deductions].sort((a, b) => {
    const aLine = DEDUCTION_FORM_LINES[a.taxDeductionClass as DeductionClass]?.schedule_c;
    const bLine = DEDUCTION_FORM_LINES[b.taxDeductionClass as DeductionClass]?.schedule_c;
    return lineSortKey(aLine) - lineSortKey(bLine);
  });

  const totalDeductions = sortedDeductions.reduce((s, d) => s + d.totalAmount, 0);
  const grossProfit = data?.grossProfit || 0;
  const netProfit = grossProfit - totalDeductions;

  // Charitable contributions get a special note (not allowed on Schedule C)
  const charitableExcluded = (data?.deductions || []).find(d => d.taxDeductionClass === 'CHARITABLE');

  function handleDownloadPDF() {
    if (!data) return;
    exportScheduleCPDF({
      businessProfile: data.businessProfile,
      taxYear,
      grossReceipts: data.grossReceipts,
      cogs: data.cogs,
      grossProfit,
      expenseLines: sortedDeductions.map(d => {
        const mapping = DEDUCTION_FORM_LINES[d.taxDeductionClass as DeductionClass];
        return {
          line: mapping?.schedule_c || '27a',
          label: mapping?.schedule_c_label || d.taxDeductionClass,
          amount: d.totalAmount,
          itemCount: d.itemCount,
        };
      }),
      totalDeductions,
      netProfit,
      charitableExcluded: charitableExcluded
        ? { amount: charitableExcluded.totalAmount }
        : undefined,
      vehicles: (data.vehicles || []).map(v => ({
        vehicleName: v.vehicleName,
        datePlacedInService: v.datePlacedInService,
        electedMethod: v.electedMethod,
        businessMiles: v.businessMiles,
        totalMiles: v.totalMiles,
        businessUsePct: v.vehicleBusinessUsePct,
        deductibleAmount: v.deductibleAmount,
      })),
    });
  }

  return (
    <>
      <Card className="print:shadow-none print:border-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Schedule C — Profit or Loss From Business
              </CardTitle>
              <CardDescription>
                IRS Form 1040, Schedule C · Tax Year {taxYear}
              </CardDescription>
            </div>
            <div className="flex gap-2 print:hidden">
              <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={!data}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" size="sm" onClick={onPrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <BusinessInfo profile={data?.businessProfile} />
          <Separator />

          {/* Part I — Income */}
          <div>
            <h3 className="font-semibold text-lg mb-3">Part I — Income</h3>
            <div className="space-y-2">
              <FormLine line="1" label="Gross receipts or sales" amount={data?.grossReceipts || 0} />
              {(data?.cogs || 0) > 0 && (
                <FormLine line="4" label="Cost of goods sold (Part III)" amount={data?.cogs || 0} negative />
              )}
              <FormLine line="7" label="Gross income" amount={grossProfit} bold />
            </div>
          </div>

          <Separator />

          {/* Part II — Expenses */}
          <div>
            <h3 className="font-semibold text-lg mb-3">Part II — Expenses</h3>
            {sortedDeductions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No deductible expenses categorized for {taxYear}</p>
                <p className="text-xs mt-1">Mark expenses as tax-deductible and assign a category in the Billing dashboard</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sortedDeductions.map((d) => {
                  const mapping = DEDUCTION_FORM_LINES[d.taxDeductionClass as DeductionClass];
                  return (
                    <FormLine
                      key={d.taxDeductionClass}
                      line={mapping?.schedule_c || '27a'}
                      label={mapping?.schedule_c_label || d.taxDeductionClass}
                      amount={d.totalAmount}
                      itemCount={d.itemCount}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {charitableExcluded && (
            <div className="p-3 rounded-md bg-blue-500/5 border border-blue-500/20 text-blue-700 text-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Charitable contributions: {formatCurrency(charitableExcluded.totalAmount)}</p>
                  <p className="text-xs mt-1">
                    Charitable contributions are not deductible on Schedule C. Sole proprietors itemize
                    them on Schedule A of Form 1040 instead.
                  </p>
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Totals */}
          <div className="space-y-3">
            <FormLine line="28" label="Total expenses" amount={totalDeductions} bold />
            <div className="flex items-center justify-between p-4 rounded bg-primary/5 border-2 border-primary/20">
              <div className="flex items-center gap-3">
                <Badge className="font-mono w-12 justify-center">31</Badge>
                <span className="font-bold text-lg">Net profit (or loss)</span>
              </div>
              <span className={`font-bold text-xl ${netProfit >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                {formatCurrency(netProfit)}
              </span>
            </div>
          </div>

          <Disclaimer />
        </CardContent>
      </Card>

      {/* Schedule C Part IV — Vehicle detail */}
      {(data?.vehicles?.length || 0) > 0 && (
        <PartIVVehicleTable vehicles={data!.vehicles} />
      )}

      {/* Schedule SE preview */}
      <ScheduleSE netProfit={netProfit} />
    </>
  );
}

// ============================================
// Form 1120 renderer (C-Corp)
// ============================================
function Form1120({
  data,
  taxYear,
  onPrint,
}: {
  data: TaxFormData | undefined;
  taxYear: number;
  onPrint: () => void;
}) {
  const allDeductions = applyDeductibilityCaps(data?.deductions || []);

  // Split into named-line deductions vs Line 26 (Other Deductions) itemization.
  const namedLineDeductions: { line: string; label: string; amount: number; classes: string[] }[] = [];
  const otherDeductions: DeductionRow[] = [];

  // Group by form_1120 line number for named lines (e.g., interest from
  // mortgage + other interest both consolidate to line 18).
  const namedGroups = new Map<string, { label: string; amount: number; classes: string[] }>();

  for (const d of allDeductions) {
    const mapping = DEDUCTION_FORM_LINES[d.taxDeductionClass as DeductionClass];
    if (!mapping) continue;
    if (mapping.form_1120 === 'OTHER') {
      otherDeductions.push(d);
    } else if (mapping.form_1120) {
      const existing = namedGroups.get(mapping.form_1120) || {
        label: mapping.form_1120_label || '',
        amount: 0,
        classes: [],
      };
      existing.amount += d.totalAmount;
      existing.classes.push(d.taxDeductionClass);
      namedGroups.set(mapping.form_1120, existing);
    }
  }

  for (const [line, group] of namedGroups.entries()) {
    namedLineDeductions.push({ line, ...group });
  }
  namedLineDeductions.sort((a, b) => parseInt(a.line) - parseInt(b.line));

  const totalNamedLines = namedLineDeductions.reduce((s, l) => s + l.amount, 0);
  const totalOther = otherDeductions.reduce((s, d) => s + d.totalAmount, 0);
  const totalDeductions = totalNamedLines + totalOther;
  const grossProfit = data?.grossProfit || 0;
  const taxableIncome = grossProfit - totalDeductions;
  const federalTax = Math.max(taxableIncome * 0.21, 0); // C-Corp flat 21%

  function handleDownloadPDF() {
    if (!data) return;
    exportForm1120PDF({
      businessProfile: data.businessProfile,
      taxYear,
      grossReceipts: data.grossReceipts,
      cogs: data.cogs,
      grossProfit,
      namedLines: namedLineDeductions.map(l => ({
        line: l.line,
        label: l.label,
        amount: l.amount,
      })),
      otherDeductions: otherDeductions.map(d => {
        const mapping = DEDUCTION_FORM_LINES[d.taxDeductionClass as DeductionClass];
        return {
          label: mapping?.form_1120_label || d.taxDeductionClass,
          amount: d.totalAmount,
          itemCount: d.itemCount,
        };
      }),
      totalOtherDeductions: totalOther,
      totalDeductions,
      taxableIncome,
      federalTax,
      vehicles: (data.vehicles || []).map(v => ({
        vehicleName: v.vehicleName,
        datePlacedInService: v.datePlacedInService,
        electedMethod: v.electedMethod,
        businessMiles: v.businessMiles,
        totalMiles: v.totalMiles,
        businessUsePct: v.vehicleBusinessUsePct,
        deductibleAmount: v.deductibleAmount,
      })),
    });
  }

  return (
    <>
      <Card className="print:shadow-none print:border-2">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Form 1120 — U.S. Corporation Income Tax Return
              </CardTitle>
              <CardDescription>
                IRS Form 1120 · Tax Year {taxYear} · Flat 21% federal rate
              </CardDescription>
            </div>
            <div className="flex gap-2 print:hidden">
              <Button variant="outline" size="sm" onClick={handleDownloadPDF} disabled={!data}>
                <Download className="h-4 w-4 mr-2" />
                Download PDF
              </Button>
              <Button variant="outline" size="sm" onClick={onPrint}>
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <BusinessInfo profile={data?.businessProfile} />
          <Separator />

          {/* Income */}
          <div>
            <h3 className="font-semibold text-lg mb-3">Income</h3>
            <div className="space-y-2">
              <FormLine line="1a" label="Gross receipts or sales" amount={data?.grossReceipts || 0} />
              {(data?.cogs || 0) > 0 && (
                <FormLine line="2" label="Cost of goods sold (Form 1125-A)" amount={data?.cogs || 0} negative />
              )}
              <FormLine line="3" label="Gross profit" amount={grossProfit} bold />
            </div>
          </div>

          <Separator />

          {/* Deductions — named lines */}
          <div>
            <h3 className="font-semibold text-lg mb-3">Deductions</h3>
            {namedLineDeductions.length === 0 && otherDeductions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No deductible expenses categorized for {taxYear}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {namedLineDeductions.map(l => (
                  <FormLine
                    key={l.line}
                    line={l.line}
                    label={l.label}
                    amount={l.amount}
                  />
                ))}

                {otherDeductions.length > 0 && (
                  <FormLine
                    line="26"
                    label="Other deductions (attach statement below)"
                    amount={totalOther}
                  />
                )}
              </div>
            )}
          </div>

          {/* Other Deductions Statement (attached to 1120 line 26) */}
          {otherDeductions.length > 0 && (
            <div className="border-2 border-dashed rounded-md p-4 bg-muted/30">
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Statement: Other Deductions (Form 1120, Line 26)
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Required itemization of deductions not assigned to a named line.
              </p>
              <div className="space-y-1.5">
                {otherDeductions
                  .sort((a, b) => b.totalAmount - a.totalAmount)
                  .map(d => {
                    const mapping = DEDUCTION_FORM_LINES[d.taxDeductionClass as DeductionClass];
                    return (
                      <div key={d.taxDeductionClass} className="flex items-center justify-between text-sm">
                        <span>
                          {mapping?.form_1120_label || d.taxDeductionClass}
                          <span className="text-xs text-muted-foreground ml-2">({d.itemCount} items)</span>
                        </span>
                        <span className="font-medium">{formatCurrency(d.totalAmount)}</span>
                      </div>
                    );
                  })}
                <Separator />
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span>Total Other Deductions</span>
                  <span>{formatCurrency(totalOther)}</span>
                </div>
              </div>
            </div>
          )}

          <Separator />

          {/* Totals */}
          <div className="space-y-3">
            <FormLine line="27" label="Total deductions" amount={totalDeductions} bold />
            <FormLine
              line="30"
              label="Taxable income (gross profit − total deductions)"
              amount={taxableIncome}
              bold
            />
            <div className="flex items-center justify-between p-4 rounded bg-primary/5 border-2 border-primary/20">
              <div className="flex items-center gap-3">
                <Badge className="font-mono w-12 justify-center">31</Badge>
                <span className="font-bold text-lg">Total tax (21% federal)</span>
              </div>
              <span className="font-bold text-xl text-destructive">
                {formatCurrency(federalTax)}
              </span>
            </div>
          </div>

          <Disclaimer />
        </CardContent>
      </Card>

      {/* Vehicle detail — useful audit support for C-Corps too even though
          Form 1120 doesn't have a dedicated Part IV section like Schedule C. */}
      {(data?.vehicles?.length || 0) > 0 && (
        <PartIVVehicleTable vehicles={data!.vehicles} />
      )}
    </>
  );
}

// ============================================
// Shared sub-components
// ============================================
function BusinessInfo({ profile }: { profile: BusinessProfile | null | undefined }) {
  return (
    <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/50">
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">Business Name</div>
        <div className="font-medium">{profile?.name || '-'}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">EIN / Tax ID</div>
        <div className="font-medium">{profile?.taxId || 'Not set'}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">City</div>
        <div className="font-medium">{profile?.city || '-'}</div>
      </div>
      <div>
        <div className="text-xs text-muted-foreground uppercase tracking-wide">State / ZIP</div>
        <div className="font-medium">
          {profile?.state || '-'} {profile?.zip || ''}
        </div>
      </div>
    </div>
  );
}

function FormLine({
  line,
  label,
  amount,
  itemCount,
  bold,
  negative,
}: {
  line: string;
  label: string;
  amount: number;
  itemCount?: number;
  bold?: boolean;
  negative?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between p-3 rounded ${bold ? 'bg-muted/50' : 'bg-muted/30'}`}>
      <div className="flex items-center gap-3">
        <Badge variant="secondary" className="font-mono w-12 justify-center">{line}</Badge>
        <div>
          <span className={bold ? 'font-semibold' : ''}>{label}</span>
          {itemCount !== undefined && (
            <span className="text-xs text-muted-foreground ml-2">({itemCount} items)</span>
          )}
        </div>
      </div>
      <span className={`${bold ? 'font-bold text-lg' : 'font-medium'} ${negative ? 'text-destructive' : ''}`}>
        {negative ? '−' : ''}{formatCurrency(amount)}
      </span>
    </div>
  );
}

function PartIVVehicleTable({ vehicles }: { vehicles: VehicleDetailRow[] }) {
  const hasMixed = vehicles.some(v => v.electedMethod === 'mixed');
  return (
    <Card className="print:shadow-none print:border-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Part IV — Vehicle Information
        </CardTitle>
        <CardDescription>
          Required when claiming car/truck expenses on Schedule C Line 9 — also useful audit support for Form 1120 filers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasMixed && (
          <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                One or more vehicles have <strong>mixed</strong> deduction methods this year (some
                expenses tagged as mileage, some as actual). The IRS requires you to elect one
                method per vehicle. Edit the conflicting expenses on the Expenses page.
              </p>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-3">Vehicle</th>
                <th className="py-2 pr-3">In Service</th>
                <th className="py-2 pr-3">Method</th>
                <th className="py-2 pr-3 text-right">Business Miles</th>
                <th className="py-2 pr-3 text-right">Total Miles</th>
                <th className="py-2 pr-3 text-right">Business Use %</th>
                <th className="py-2 pr-3 text-right">Deductible</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map(v => {
                const computedPct = v.totalMiles > 0
                  ? Math.round((v.businessMiles / v.totalMiles) * 1000) / 10
                  : null;
                const displayPct = v.vehicleBusinessUsePct ?? computedPct;
                return (
                  <tr key={v.assetId} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-medium">{v.vehicleName}</td>
                    <td className="py-2 pr-3 text-muted-foreground">
                      {v.datePlacedInService
                        ? new Date(v.datePlacedInService).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="py-2 pr-3">
                      {v.electedMethod ? (
                        <Badge
                          variant={v.electedMethod === 'mixed' ? 'destructive' : 'secondary'}
                          className="capitalize"
                        >
                          {v.electedMethod}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {v.businessMiles.toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {v.totalMiles > 0 ? v.totalMiles.toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {displayPct !== null ? `${displayPct}%` : '—'}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums font-medium text-emerald-600">
                      {formatCurrency(v.deductibleAmount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Business-use % is taken from the asset's setting when configured; otherwise computed from
          mileage (business / total). The IRS requires written contemporaneous records (mileage log)
          for vehicle deductions.
        </p>
      </CardContent>
    </Card>
  );
}

function ScheduleSE({ netProfit }: { netProfit: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Schedule SE — Self-Employment Tax</CardTitle>
          <CardDescription>Calculated from Schedule C net profit</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm">Net earnings from self-employment</span>
              <span className="font-medium">{formatCurrency(netProfit)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">92.35% of net earnings</span>
              <span className="font-medium">{formatCurrency(netProfit * 0.9235)}</span>
            </div>
            <Separator />
            <div className="flex justify-between">
              <span className="text-sm font-semibold">Self-employment tax (15.3%)</span>
              <span className="font-bold">{formatCurrency(netProfit * 0.9235 * 0.153)}</span>
            </div>
            <div className="flex justify-between text-emerald-600">
              <span className="text-sm">Deductible half of SE tax</span>
              <span className="font-medium">{formatCurrency(netProfit * 0.9235 * 0.153 / 2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">1099 Income Tracking</CardTitle>
          <CardDescription>Revenue from clients who should issue 1099-NEC</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              Revenue tracked in your invoices can be cross-referenced with 1099-NEC forms received from clients.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Disclaimer() {
  return (
    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
      <div className="flex items-start gap-2">
        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">For Reference Only</p>
          <p className="text-xs mt-1">
            This is an aggregated summary based on your recorded expenses and revenue.
            It is not an official IRS form. Consult your CPA or tax professional for actual filing.
            Print or export this data to share with your accountant.
          </p>
        </div>
      </div>
    </div>
  );
}
